import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import mmrService, { ClientMMRProfile, MMRHistoryItem } from '@/services/mmrService';

interface TierDefinition {
  name: string;
  rangeText: string;
  minMMR: number;
  maxMMR: number;
  iconBg: string;
  iconColor: string;
}

const TIER_LIST: TierDefinition[] = [
  {
    name: 'Bronze',
    rangeText: '0 - 1.999',
    minMMR: 0,
    maxMMR: 1999,
    iconBg: 'bg-amber-900/10 border-amber-800/20 text-amber-700',
    iconColor: 'text-amber-700',
  },
  {
    name: 'Silver',
    rangeText: '2.000 - 3.999',
    minMMR: 2000,
    maxMMR: 3999,
    iconBg: 'bg-slate-200 border-slate-300 text-slate-600',
    iconColor: 'text-slate-600',
  },
  {
    name: 'Gold',
    rangeText: '4.000 - 5.999',
    minMMR: 4000,
    maxMMR: 5999,
    iconBg: 'bg-amber-100 border-amber-300 text-amber-600',
    iconColor: 'text-amber-600',
  },
  {
    name: 'Master',
    rangeText: '6.000 - 7.999',
    minMMR: 6000,
    maxMMR: 7999,
    iconBg: 'bg-purple-100 border-purple-300 text-purple-600',
    iconColor: 'text-purple-600',
  },
  {
    name: 'Elite',
    rangeText: '8.000 - 9.999',
    minMMR: 8000,
    maxMMR: 9999,
    iconBg: 'bg-blue-100 border-blue-300 text-blue-600',
    iconColor: 'text-blue-600',
  },
  {
    name: 'Immortal',
    rangeText: '10.000+',
    minMMR: 10000,
    maxMMR: 99999,
    iconBg: 'bg-rose-100 border-rose-300 text-rose-600',
    iconColor: 'text-rose-600',
  },
];

const ClientMMRDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const [mmrProfile, setMmrProfile] = useState<ClientMMRProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchMMR = async () => {
      try {
        setIsLoading(true);
        const res = await mmrService.getClientMMRProfile('invasion');
        if (res.success && res.data) {
          setMmrProfile(res.data);
        }
      } catch (err) {
        showToast.error('Gagal memuat detail MMR');
      } finally {
        setIsLoading(false);
      }
    };
    fetchMMR();
  }, []);

  const currentMMR = mmrProfile?.mmr ?? 0;
  const currentTierName = mmrProfile?.tier || 'Bronze';
  const targetMMR = mmrProfile?.targetMMR ?? 2000;
  const tierMinMMR = mmrProfile?.tierMinMMR ?? 0;
  const neededMMR = mmrProfile?.neededMMR ?? 0;
  const nextTierName = mmrProfile?.nextTier || 'Silver';
  const rankDisplay = mmrProfile?.rankDisplay || '-';

  // Progress Bar percentage calculation
  const progressPercent = targetMMR > tierMinMMR
    ? Math.min(100, Math.max(0, ((currentMMR - tierMinMMR) / (targetMMR - tierMinMMR)) * 100))
    : 100;

  // History list (Limit 5)
  const historyList: MMRHistoryItem[] = (mmrProfile?.history || []).slice(0, 5);

  return (
    <div className="bg-sporto-bg min-h-screen text-sporto-dark py-5 pb-12">
      {/* Desktop Breadcrumb Header (Hidden on Mobile as MobileTopbar handles topbar) */}
      <div className="hidden md:block bg-white border-b border-[#E0E6ED] px-6 py-4 mb-6 shadow-2xs">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="w-9 h-9 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-[#0E1726]">Detail MMR</h1>
            <p className="text-xs text-gray-500">Statistik & Riwayat MMR Pemain</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-18 md:pt-2 space-y-5">
        {/* 1. TOP MMR CARD (Radial Gradient Dark Card Layout) */}
        {isLoading ? (
          <div
            className="shadow-md text-white overflow-hidden transition-all space-y-4 animate-pulse"
            style={{
              borderRadius: '18px',
              padding: '16px',
              background: 'radial-gradient(120% 90% at 100% 0%, rgb(37, 49, 74) 0%, rgb(14, 23, 38) 65%)',
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-slate-800/80" />
                <div className="space-y-1.5">
                  <div className="h-5 w-24 bg-slate-700/80 rounded" />
                  <div className="h-3 w-32 bg-slate-800/80 rounded" />
                </div>
              </div>
              <div className="space-y-1 text-right">
                <div className="h-2.5 w-16 bg-slate-800/80 rounded ml-auto" />
                <div className="h-4 w-24 bg-slate-700/80 rounded ml-auto" />
              </div>
            </div>
            <div className="h-9 w-32 bg-slate-700/80 rounded-lg" />
            <div className="space-y-2 pt-1">
              <div className="w-full h-2.5 bg-slate-800/80 rounded-full" />
              <div className="flex justify-between">
                <div className="h-3 w-28 bg-slate-800/80 rounded" />
                <div className="h-3 w-24 bg-slate-800/80 rounded" />
              </div>
            </div>
          </div>
        ) : (
          <div
            className="shadow-md text-white overflow-hidden transition-all space-y-4 relative"
            style={{
              borderRadius: '18px',
              padding: '16px',
              background: 'radial-gradient(120% 90% at 100% 0%, rgb(37, 49, 74) 0%, rgb(14, 23, 38) 65%)',
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/10">
                  <Icon icon="ph:shield-bold" className="w-6 h-6 text-slate-300" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white leading-tight">
                    {currentTierName}
                  </h2>
                  <span className="text-xs text-slate-400 font-medium">
                    Rentang {Math.round(tierMinMMR).toLocaleString('id-ID')} - {targetMMR === 10000 ? '10.000+' : Math.round(targetMMR - 1).toLocaleString('id-ID')}
                  </span>
                </div>
              </div>

              <div className="text-right">
                <span className="text-[9px] uppercase font-bold tracking-widest text-slate-400 block">
                  PERINGKAT
                </span>
                <span className="text-sm font-bold text-white leading-tight">
                  {rankDisplay}
                </span>
              </div>
            </div>

            {/* MMR Value & Monthly Delta */}
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-extrabold text-white tracking-tight">
                  {Math.round(currentMMR).toLocaleString('id-ID')}
                </span>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-semibold text-slate-400">MMR</span>
                  <span className="text-xs font-bold text-emerald-400 flex items-center gap-0.5 ml-1">
                    <Icon icon="ph:arrow-up-bold" className="w-3 h-3" />
                    {(mmrProfile?.delta30Days ?? 0) >= 0
                      ? `+${Math.round(mmrProfile?.delta30Days ?? 0).toLocaleString('id-ID')}`
                      : Math.round(mmrProfile?.delta30Days ?? 0).toLocaleString('id-ID')}{' '}
                    bulan ini
                  </span>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-1.5 pt-1">
              <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden p-0.5">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-400 font-semibold pt-0.5">
                <span className="uppercase tracking-wide">
                  {currentTierName} · {Math.round(tierMinMMR).toLocaleString('id-ID')}
                </span>
                <span className="uppercase tracking-wide text-slate-300">
                  {nextTierName} · {Math.round(targetMMR).toLocaleString('id-ID')}
                </span>
              </div>
              <p className="text-xs text-slate-300 font-medium pt-1">
                {currentMMR >= 10000 ? (
                  'Kamu telah mencapai Tier Tertinggi (Immortal)!'
                ) : (
                  <>
                    Butuh <span className="font-bold text-white">{Math.round(neededMMR).toLocaleString('id-ID')} MMR</span> lagi untuk naik ke {nextTierName}
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        {/* 2. RINGKASAN 30 HARI (4 Grid Cards) */}
        <div className="space-y-3">
          <h2 className="text-base font-bold text-slate-900">Ringkasan 30 hari</h2>
          <div className="grid grid-cols-2 gap-3">
            {/* Poin didapat */}
            <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                <Icon icon="ph:trend-up-bold" className="w-3.5 h-3.5 text-emerald-500" />
                <span>Poin didapat</span>
              </div>
              <p className="text-2xl font-extrabold text-emerald-500">
                +{Math.round(mmrProfile?.pointsGained30Days ?? 0).toLocaleString('id-ID')}
              </p>
              <p className="text-xs text-slate-400 font-medium">
                {mmrProfile?.winDrawMatches30Days ?? 0} game menang / seri
              </p>
            </div>

            {/* Poin hilang */}
            <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                <Icon icon="ph:trend-down-bold" className="w-3.5 h-3.5 text-rose-500" />
                <span>Poin hilang</span>
              </div>
              <p className="text-2xl font-extrabold text-rose-500">
                {(mmrProfile?.pointsLost30Days ?? 0) <= 0
                  ? Math.round(mmrProfile?.pointsLost30Days ?? 0).toLocaleString('id-ID')
                  : `-${Math.round(mmrProfile?.pointsLost30Days ?? 0).toLocaleString('id-ID')}`}
              </p>
              <p className="text-xs text-slate-400 font-medium">
                {mmrProfile?.lossMatches30Days ?? 0} game kalah
              </p>
            </div>

            {/* Net bulan ini */}
            <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                <Icon icon="ph:equals-bold" className="w-3.5 h-3.5 text-slate-600" />
                <span>Net bulan ini</span>
              </div>
              <p className="text-2xl font-extrabold text-slate-900">
                {(mmrProfile?.delta30Days ?? 0) >= 0
                  ? `+${Math.round(mmrProfile?.delta30Days ?? 0).toLocaleString('id-ID')}`
                  : Math.round(mmrProfile?.delta30Days ?? 0).toLocaleString('id-ID')}
              </p>
              <p className="text-xs text-slate-400 font-medium">
                {mmrProfile?.totalMatches30Days ?? 0} game dimainkan
              </p>
            </div>

            {/* Rata-rata / game */}
            <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                <Icon icon="ph:chart-line-up-bold" className="w-3.5 h-3.5 text-amber-500" />
                <span>Rata-rata / game</span>
              </div>
              <p className="text-2xl font-extrabold text-slate-900">
                {(mmrProfile?.avgDeltaPerMatch30Days ?? 0) >= 0
                  ? `+${Math.round(mmrProfile?.avgDeltaPerMatch30Days ?? 0).toLocaleString('id-ID')}`
                  : Math.round(mmrProfile?.avgDeltaPerMatch30Days ?? 0).toLocaleString('id-ID')}
              </p>
              <p className="text-xs text-slate-400 font-medium">
                Tertinggi +{Math.round(mmrProfile?.highestDelta30Days ?? 0).toLocaleString('id-ID')}
              </p>
            </div>
          </div>
        </div>

        {/* 3. RIWAYAT POIN MMR (5 Terbaru) */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900">Riwayat poin MMR (5 Terbaru)</h2>
            <span className="text-xs font-medium text-slate-400">Terbaru &rarr; Terlama</span>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs divide-y divide-slate-100 overflow-hidden">
            {historyList.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-xs">
                Belum ada riwayat pertandingan MMR.
              </div>
            ) : (
              historyList.map((item, index) => {
                const isClickable = Boolean(item.eventSlug && item.gameSlug);
                return (
                  <div
                    key={item.id}
                    onClick={() => {
                      if (isClickable) {
                        navigate(`/event/${item.eventSlug}/game/${item.gameSlug}`);
                      }
                    }}
                    className={`p-3.5 flex items-center justify-between transition-colors ${
                      isClickable ? 'hover:bg-slate-50/80 cursor-pointer' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Index Order Badge */}
                      <span className="w-5 text-center text-xs font-bold text-slate-400 shrink-0">
                        #{index + 1}
                      </span>

                      {/* Date Badge */}
                      <div className="w-11 h-11 rounded-xl bg-slate-100 border border-slate-200/60 flex flex-col items-center justify-center shrink-0">
                        <span className="text-[9px] uppercase font-bold text-slate-400 leading-none">
                          {item.date.split(' ')[1] || 'JUL'}
                        </span>
                        <span className="text-sm font-bold text-slate-800 leading-none pt-0.5">
                          {item.date.split(' ')[0] || '24'}
                        </span>
                      </div>

                      <div>
                        <h3 className="text-sm font-bold text-slate-900 leading-snug line-clamp-1">
                          {item.title}
                        </h3>
                        <div className="flex items-center gap-1.5 text-xs pt-0.5">
                          <span
                            className={`font-semibold ${
                              item.outcomeStatus === 'win'
                                ? 'text-emerald-600'
                                : item.outcomeStatus === 'loss'
                                ? 'text-rose-600'
                                : 'text-slate-600'
                            }`}
                          >
                            {item.outcomeStatus === 'draw' && (!item.outcomeText || item.outcomeText === 'Seri')
                              ? 'Imbang'
                              : item.outcomeText}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <span
                          className={`text-base font-extrabold ${
                            item.deltaMMR > 0
                              ? 'text-emerald-500'
                              : item.deltaMMR < 0
                              ? 'text-rose-500'
                              : 'text-slate-400'
                          }`}
                        >
                          {item.deltaMMR > 0 ? `+${Math.round(item.deltaMMR)}` : Math.round(item.deltaMMR)}
                        </span>
                      </div>
                      {isClickable && (
                        <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-slate-300" />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* 4. TIER & RENTANG MMR */}
        <div className="space-y-3">
          <h2 className="text-base font-bold text-slate-900">Tier & rentang MMR</h2>
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs divide-y divide-slate-100 overflow-hidden">
            {TIER_LIST.map((t) => {
              const isCurrentTier = currentTierName.toLowerCase() === t.name.toLowerCase();
              return (
                <div
                  key={t.name}
                  className={`p-3.5 flex items-center justify-between transition-colors ${
                    isCurrentTier ? 'bg-orange-50/50 border-l-4 border-l-orange-500' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center border ${t.iconBg}`}>
                      <Icon icon="ph:shield-bold" className={`w-5 h-5 ${t.iconColor}`} />
                    </div>
                    <span className={`text-sm font-bold ${isCurrentTier ? 'text-slate-900' : 'text-slate-700'}`}>
                      {t.name}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-400">
                      {t.rangeText}
                    </span>
                    {isCurrentTier && (
                      <span className="px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold tracking-wider uppercase">
                        KAMU
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 5. CARA MMR DIHITUNG */}
        <div className="bg-orange-50/60 rounded-2xl p-4 border border-orange-100 space-y-1.5 flex gap-3 items-start">
          <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5">
            <Icon icon="ph:info-bold" className="w-3.5 h-3.5 text-orange-500" />
          </div>
          <div className="space-y-1">
            <h3 className="text-xs font-bold text-slate-900">Cara MMR dihitung</h3>
            <p className="text-xs text-slate-600 leading-relaxed font-normal">
              MMR ditentukan tiga hal: hasil pertandingan, kontribusi performa kamu di game itu, dan tingkat kesulitan dari MMR rata-rata tim lawan. Menang lawan tim lebih kuat memberi poin lebih besar.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientMMRDetailPage;
